import React, { useEffect } from "react";
import { Container, Row, Col, Button, Card, Carousel } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "../App.css";

const HomeScreen = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  // ✅ Redirect logged-in users to their dashboards
  useEffect(() => {
    if (userInfo) {
      const role = userInfo.user.role;
      if (role === "admin") navigate("/admin/dashboard");
      else if (role === "doctor") navigate("/doctor/dashboard");
      else if (role === "patient") navigate("/patient/appointments");
      else if (role === "pharmacist") navigate("/counter");
      else if (role === "receptionist") navigate("/receptionist/walk-in");
    }
  }, [userInfo, navigate]);

  return (
    <>
      {/* 🖼️ Hero Slider Section */}
      <section className="hero-slider">
        <Carousel fade interval={3500} pause={false}>
          <Carousel.Item>
            <img
              className="d-block w-100 slider-img"
              src="https://img.freepik.com/free-photo/doctor-nurses-special-equipment_23-2148980721.jpg"
              alt="Hospital Management System"
            />
            <Carousel.Caption className="text-start">
              <h1 className="fw-bold">Welcome to Zainab Medical & Children Care Centre</h1>
              <p>Quality healthcare made simple, safe, and accessible.</p>
              <Button
                variant="light"
                size="lg"
                onClick={() => navigate("/login")}
              >
                Get Started
              </Button>
            </Carousel.Caption>
          </Carousel.Item>

          <Carousel.Item>
            <img
              className="d-block w-100 slider-img"
              src="https://img.freepik.com/free-photo/doctor-patient-talking-hospital_23-2148962455.jpg"
              alt="Digital Healthcare Management"
            />
            <Carousel.Caption className="text-start">
              <h1 className="fw-bold">Book Appointments Online</h1>
              <p>Meet the right doctor at the right time — without the wait.</p>
              <Button
                variant="light"
                size="lg"
                onClick={() => navigate("/register")}
              >
                Join Now
              </Button>
            </Carousel.Caption>
          </Carousel.Item>

          <Carousel.Item>
            <img
              className="d-block w-100 slider-img"
              src="https://img.freepik.com/free-photo/medical-banner-with-doctor-wearing-goggles_23-2149611193.jpg"
              alt="Modern Hospital Management"
            />
            <Carousel.Caption className="text-start">
              <h1 className="fw-bold">Modern Facilities, Expert Doctors</h1>
              <p>
                Experience world-class medical care — right at your fingertips.
              </p>
              <Button
                variant="light"
                size="lg"
                onClick={() => navigate("/contact")}
              >
                Contact Us
              </Button>
            </Carousel.Caption>
          </Carousel.Item>
        </Carousel>
      </section>

      {/* 🩺 About Section */}
      <section className="about-section py-5">
        <Container>
          <Row className="align-items-center">
            <Col md={6}>
              <img
                src="https://img.freepik.com/free-vector/doctor-character-background_1270-84.jpg"
                alt="About us"
                className="img-fluid rounded shadow"
              />
            </Col>
            <Col md={6}>
              <h2 className="fw-bold mb-3 text-gradient">About Our Hospital</h2>
              <p className="text-muted">
                At Zainab Medical & Children Care Centre, we believe in providing reliable,
                affordable, and quality healthcare. Our platform lets you easily
                schedule appointments, view reports, and manage prescriptions.
              </p>
              <Button
                variant="primary"
                className="mt-3"
                onClick={() => navigate("/register")}
              >
                Learn More
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 🧑‍⚕️ Services Section */}
      <section className="services-section py-5 text-center">
        <Container>
          <h2 className="fw-bold mb-5 text-gradient">Our Services</h2>
          <Row>
            {[
              {
                title: "Book Appointments",
                desc: "Easily schedule appointments with top doctors.",
                icon: "bi bi-calendar2-check",
              },
              {
                title: "Lab Reports",
                desc: "Access and download your medical reports instantly.",
                icon: "bi bi-file-earmark-medical",
              },
              {
                title: "Order Medicines",
                desc: "Order your prescribed medicines from trusted pharmacies.",
                icon: "bi bi-capsule",
              },
              {
                title: "Patient Records",
                desc: "Secure digital storage of all your medical history.",
                icon: "bi bi-folder2-open",
              },
              {
                title: "Emergency Care",
                desc: "24/7 emergency services for critical health situations.",
                icon: "bi bi-hospital",
              },
              {
                title: "Children Care",
                desc: "Specialized pediatric care for your little ones.",
                icon: "bi bi-heart-pulse",
              },
            ].map((service, index) => (
              <Col md={4} key={index} className="mb-4">
                <Card className="service-card h-100 shadow-sm border-0">
                  <Card.Body>
                    <i className={`${service.icon} service-icon mb-3`}></i>
                    <Card.Title className="fw-bold">{service.title}</Card.Title>
                    <Card.Text className="text-muted">{service.desc}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* 📊 Statistics Section */}
      <section className="stats-section py-5 bg-primary text-white">
        <Container>
          <Row className="text-center">
            {[
              { number: "5000+", label: "Patients Served" },
              { number: "50+", label: "Expert Doctors" },
              { number: "10+", label: "Years of Service" },
              { number: "24/7", label: "Emergency Support" },
            ].map((stat, i) => (
              <Col md={3} sm={6} key={i} className="mb-3">
                <h2 className="fw-bold display-4">{stat.number}</h2>
                <p className="mb-0">{stat.label}</p>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* 🏥 Why Choose Us */}
      <section className="why-choose-section py-5">
        <Container>
          <h2 className="fw-bold mb-5 text-center text-gradient">Why Choose Us</h2>
          <Row>
            {[
              {
                title: "Experienced Team",
                desc: "Our medical staff has years of experience in their respective fields.",
                icon: "bi bi-people-fill",
              },
              {
                title: "Advanced Technology",
                desc: "State-of-the-art medical equipment and digital healthcare management.",
                icon: "bi bi-laptop-fill",
              },
              {
                title: "Affordable Care",
                desc: "Quality healthcare that doesn't break your budget.",
                icon: "bi bi-cash-coin",
              },
              {
                title: "Patient-Centered",
                desc: "Your health and comfort are our top priorities.",
                icon: "bi bi-heart-fill",
              },
            ].map((item, i) => (
              <Col md={6} lg={3} key={i} className="mb-4">
                <div className="text-center">
                  <i className={`${item.icon} text-primary`} style={{ fontSize: "3rem" }}></i>
                  <h5 className="fw-bold mt-3">{item.title}</h5>
                  <p className="text-muted">{item.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* 💬 Testimonials */}
      <section className="testimonials-section py-5 bg-light text-center">
        <Container>
          <h2 className="fw-bold mb-5 text-gradient">What Our Patients Say</h2>
          <Row>
            {[
              {
                name: "Ali Khan",
                feedback:
                  "Amazing experience! The online appointment system saved me so much time.",
              },
              {
                name: "Sara Ahmed",
                feedback:
                  "Got my reports and medicines without leaving home. Highly recommended!",
              },
              {
                name: "Usman Tariq",
                feedback:
                  "User-friendly and reliable. Best healthcare platform in KP.",
              },
            ].map((testimonial, i) => (
              <Col md={4} key={i} className="mb-4">
                <Card className="p-3 border-0 shadow-sm rounded-4">
                  <Card.Body>
                    <p className="fst-italic text-muted">
                      "{testimonial.feedback}"
                    </p>
                    <h6 className="fw-bold mt-3">{testimonial.name}</h6>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* 📞 Contact Details Section */}
      <section className="contact-details-section py-5">
        <Container>
          <h2 className="fw-bold mb-5 text-center text-gradient">Get In Touch</h2>
          <Row className="justify-content-center">
            <Col md={4} className="mb-4">
              <Card className="text-center border-0 shadow-sm h-100">
                <Card.Body>
                  <i className="bi bi-telephone-fill text-primary" style={{ fontSize: "2.5rem" }}></i>
                  <h5 className="fw-bold mt-3">Call Us</h5>
                  <p className="text-muted mb-1">+923152189910</p>
                  <small className="text-muted">(WhatsApp Only)</small>
                  <p className="text-muted mt-2 mb-0">+923153051011</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="text-center border-0 shadow-sm h-100">
                <Card.Body>
                  <i className="bi bi-geo-alt-fill text-primary" style={{ fontSize: "2.5rem" }}></i>
                  <h5 className="fw-bold mt-3">Visit Us</h5>
                  <p className="text-muted mb-0">
                    GT Road Darwaish<br />
                    Near New Bus Stand<br />
                    Haripur, KP
                  </p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="text-center border-0 shadow-sm h-100">
                <Card.Body>
                  <i className="bi bi-clock-fill text-primary" style={{ fontSize: "2.5rem" }}></i>
                  <h5 className="fw-bold mt-3">Working Hours</h5>
                  <p className="text-muted mb-1">Monday - Saturday</p>
                  <p className="text-muted mb-1">9:00 AM - 9:00 PM</p>
                  <p className="text-muted mb-0">
                    <small>Emergency: 24/7</small>
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* 📞 Contact Section */}
      <section className="contact-section py-5 text-center text-white">
        <Container>
          <h2 className="fw-bold mb-3">Need Assistance?</h2>
          <p className="mb-4">
            Reach out to our support team for help or queries anytime.
          </p>
          <Button
            variant="light"
            size="lg"
            onClick={() => navigate("/contact")}
          >
            Contact Us
          </Button>
        </Container>
      </section>
    </>
  );
};

export default HomeScreen;